import { useEffect, useRef, useState } from 'react';

export default function DispatchSignatureModal({ show, loading, onClose, onConfirm, color }) {
    const canvasRef = useRef(null);
    const [recipientName, setRecipientName] = useState('');
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);

    useEffect(() => {
        if (!show || !canvasRef.current) return;
        setRecipientName('');
        setHasSignature(false);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.strokeStyle = '#111111';
        context.lineWidth = 3;
        context.lineCap = 'round';
        context.lineJoin = 'round';
    }, [show]);

    if (!show) return null;

    const position = (event) => {
        const canvas = canvasRef.current;
        const bounds = canvas.getBoundingClientRect();
        return {
            x: (event.clientX - bounds.left) * (canvas.width / bounds.width),
            y: (event.clientY - bounds.top) * (canvas.height / bounds.height),
        };
    };

    const startDrawing = (event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        const context = canvasRef.current.getContext('2d');
        const point = position(event);
        context.beginPath();
        context.moveTo(point.x, point.y);
        setIsDrawing(true);
    };

    const draw = (event) => {
        if (!isDrawing) return;
        event.preventDefault();
        const context = canvasRef.current.getContext('2d');
        const point = position(event);
        context.lineTo(point.x, point.y);
        context.stroke();
        setHasSignature(true);
    };

    const stopDrawing = (event) => {
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
        setIsDrawing(false);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');
        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const confirmDispatch = () => {
        if (!recipientName.trim() || !hasSignature) return;
        onConfirm({
            source: 'device',
            recipient_name: recipientName.trim(),
            signature_data: canvasRef.current.toDataURL('image/png'),
        });
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1100,
            background: 'rgba(0, 0, 0, 0.65)', display: 'grid', placeItems: 'center', padding: 16,
        }}>
            <div role="dialog" aria-modal="true" aria-labelledby="dispatch-signature-title" style={{
                width: 'min(560px, 100%)', maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
                background: color.bg, color: color.fg, border: `1px solid ${color.border}`,
                borderRadius: 6, padding: 20,
            }}>
                <h3 id="dispatch-signature-title" style={{ marginTop: 0 }}>Confirm Dispatch</h3>

                <label htmlFor="dispatch-recipient-name" style={{ display: 'block', marginBottom: 6 }}>
                    Recipient name
                </label>
                <input
                    id="dispatch-recipient-name"
                    type="text"
                    maxLength={200}
                    autoComplete="name"
                    value={recipientName}
                    onChange={(event) => setRecipientName(event.target.value)}
                    disabled={loading}
                    style={{
                        width: '100%', boxSizing: 'border-box', padding: 12, marginBottom: 18,
                        background: color.bg, color: color.fg, border: `1px solid ${color.border}`,
                        borderRadius: 4, fontSize: 16,
                    }}
                />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <span>Recipient signature</span>
                    <button type="button" onClick={clearSignature} disabled={loading || !hasSignature}>
                        Clear
                    </button>
                </div>
                <canvas
                    ref={canvasRef}
                    width={1000}
                    height={360}
                    onPointerDown={startDrawing}
                    onPointerMove={draw}
                    onPointerUp={stopDrawing}
                    onPointerCancel={stopDrawing}
                    style={{
                        display: 'block', width: '100%', aspectRatio: '25 / 9',
                        background: '#ffffff', border: `2px solid ${color.border}`,
                        borderRadius: 4, touchAction: 'none', cursor: 'crosshair',
                    }}
                />

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                    <button type="button" onClick={onClose} disabled={loading} style={{ padding: '10px 16px' }}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={confirmDispatch}
                        disabled={loading || !recipientName.trim() || !hasSignature}
                        style={{
                            padding: '10px 16px', border: 0, borderRadius: 4,
                            background: color.primary, color: '#ffffff',
                            opacity: loading || !recipientName.trim() || !hasSignature ? 0.55 : 1,
                        }}
                    >
                        {loading ? 'Dispatching...' : 'Confirm Dispatch'}
                    </button>
                </div>
            </div>
        </div>
    );
}